import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
import { betriebstageBuchService } from "../services/api"; // Combined
import { pdfService } from "../services/pdfService";
import { AuthContext } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import SignatureCanvas from "react-signature-canvas";
import moment from "moment";

// MUI Components
import {
  Box, Button, Card, CardContent, CardHeader, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Divider, Grid, Paper, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, Stack, Alert, AlertTitle, List, ListItem, ListItemText, ListItemIcon, useTheme, FormHelperText,
  Avatar, IconButton
} from "@mui/material";
import { alpha } from "@mui/material/styles";

// MUI Icons
import {
  CheckOutlined as CheckIcon, ClearOutlined as ClearIcon, EditOutlined as EditIcon, DownloadOutlined as DownloadIcon,
  ArrowBackOutlined as ArrowBackIcon, CheckCircleOutline as CheckCircleIcon, WarningAmberOutlined as WarningIcon,
  InfoOutlined as InfoIcon, CalendarTodayOutlined as CalendarMonthIcon, PersonOutline as PersonIcon, // Outlined
  ListAltOutlined as ListAltIcon, DescriptionOutlined as DescriptionIcon, BuildCircleOutlined as BuildIcon, // Outlined
  DrawOutlined as DrawIcon, // Outlined for signature
  CloseOutlined as CloseDialogIcon, Close as CloseIcon, // For dialog close and general close
  VerifiedOutlined as VerifiedIcon, // For reviewed by
  AccountCircleOutlined as AccountCircleIcon, // For profile signature toggle
  PendingOutlined as PendingIcon, // For pending status
  BuildOutlined as ConveyorIcon, // For conveyor checks
  PlaylistAddCheckOutlined as SpecialCheckIcon, // For special checks
  NotesOutlined as NotesIcon, // For notes
} from "@mui/icons-material";

import PageContainer from "../components/PageContainer";
// ContentSection is used, but the top-level page actions might be outside it for a cleaner header.

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`btb-detail-tabpanel-${index}`} aria-labelledby={`btb-detail-tab-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 2.5 }}>{children}</Box>}
    </div>
  );
}

const DetailItem = ({ label, value, icon, chipProps }) => (
  <ListItem disablePadding sx={{ py: 0.75 }}>
    {icon && <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>{React.cloneElement(icon, { fontSize: 'small' })}</ListItemIcon>}
    <ListItemText
      primary={label}
      secondary={chipProps ? <Chip size="small" {...chipProps} /> : (value || '-')}
      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
      secondaryTypographyProps={{ variant: 'body1', fontWeight: 'medium', color: 'text.primary' }}
    />
  </ListItem>
);


const BetriebstageBuchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, isManager } = useContext(AuthContext);
  const { t } = useLanguage();
  const theme = useTheme();
  const signatureRef = useRef(null);

  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [signatureData, setSignatureData] = useState(null); // For storing actual signature data
  const [useProfileSigForApproval, setUseProfileSigForApproval] = useState(true);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [pdfDownloadLoading, setPdfDownloadLoading] = useState(false);


  const fetchEntry = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await betriebstageBuchService.getById(id);
      setEntry(response.data);
      if (currentUser?.signature) {
        setUseProfileSigForApproval(true);
        setSignatureData(currentUser.signature); // Set signature data automatically
      } else {
        setUseProfileSigForApproval(false);
        setSignatureData(null);
      }
    } catch (err) {
      console.error("Error fetching entry:", err);
      setError(t("errorFetchingEntry") + (err.message ? `: ${err.message}` : ''));
    } finally { setLoading(false); }
  }, [id, t, currentUser?.signature]);

  useEffect(() => { fetchEntry(); }, [fetchEntry]);

  const handleDownloadPDF = async () => {
    setPdfDownloadLoading(true); setError(null);
    try { await pdfService.downloadBetriebstageBuchPDF(id); } // Using the correct service
    catch (err) { console.error("Error downloading PDF:", err); setError(t("errorDownloadingPDF")); }
    finally { setPdfDownloadLoading(false); }
  };

  const handleModalSignatureConfirm = () => {
    let sigData = null;
    setApproveError(null);
    if (useProfileSigForApproval && currentUser?.signature) {
      sigData = currentUser.signature;
    } else {
      if (!signatureRef.current || signatureRef.current.isEmpty()) {
        setApproveError(t("signatureRequired")); return false;
      }
      sigData = signatureRef.current.getTrimmedCanvas().toDataURL("image/png");
    }
    if (!sigData) { setApproveError(t("signatureRequired")); return false; }
    setSignatureData(sigData); // Store confirmed signature data
    return true;
  };

  const clearModalSignature = () => { if (signatureRef.current) signatureRef.current.clear(); setSignatureData(null); setApproveError(null); };

  const handleApprove = async () => {
    if (!handleModalSignatureConfirm()) return; // Validate and set signatureData

    setApproveLoading(true); setApproveError(null);
    try {
      await betriebstageBuchService.approve(id, { reviewerName: currentUser.name, reviewerSignature: signatureData });
      setShowApproveModal(false); fetchEntry();
    } catch (err) {
      console.error("Error approving entry:", err);
      setApproveError(t("errorApprovingEntry") + `: ${err.response?.data?.message || err.message}`);
    } finally { setApproveLoading(false); }
  };

  const getStatusChipProps = (status) => {
    switch (status?.toLowerCase()) {
      case "approved": return { color: "success", icon: <CheckCircleIcon />, label: t('approved') };
      case "pending": return { color: "warning", icon: <PendingIcon />, label: t('pending') };
      default: return { color: "default", icon: <InfoIcon />, label: t(status || "unknownStatus") };
    }
  };

  const renderCheckStatusIcon = (isOk) => (
    <Avatar sx={{ width: 28, height: 28, bgcolor: isOk ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.error.main, 0.15), color: isOk ? "success.dark" : "error.dark" }}>
      {isOk ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
    </Avatar>
  );

  if (loading) return <PageContainer title={t("loading")}><Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box></PageContainer>;
  if (error && !entry) return <PageContainer title={t("error")}><Alert severity="error" sx={{ m: 2 }}><AlertTitle>{t("error")}</AlertTitle>{error}</Alert><Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ m: 2 }}>{t("back")}</Button></PageContainer>;
  if (!entry) return <PageContainer title={t("notFound")}><Alert severity="warning" sx={{ m: 2 }}>{t("entryNotFound")}</Alert><Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ m: 2 }}>{t("back")}</Button></PageContainer>;

  const { icon: statusIcon, color: statusColor, label: statusLabel } = getStatusChipProps(entry.status);

  return (
    <PageContainer title={`${t("betriebstagebuchEntry")} #${entry._id.slice(-6)}`}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: theme.shape.borderRadius * 1.5, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.background.paper, 0.3)} 100%)`, border: `1px solid ${theme.palette.divider}` }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md>
            <Stack direction="row" alignItems="center" spacing={2} mb={1.5}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), color: "primary.main", width: 56, height: 56 }}><ListAltIcon fontSize="large" /></Avatar>
              <div>
                <Typography variant="h5" component="h1" fontWeight="bold">{t("betriebstagebuchEntry")}</Typography>
                <Typography variant="body2" color="text.secondary">{t("date")}: {moment(entry.date).format("LL")}</Typography>
              </div>
            </Stack>
            <Chip icon={statusIcon} label={statusLabel} color={statusColor} size="medium" />
          </Grid>
          <Grid item xs={12} md="auto">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
              <Button variant="outlined" color="secondary" startIcon={<ArrowBackIcon />} onClick={() => navigate("/betriebstagebuch")}>{t("backToList")}</Button>
              {isManager() && entry.status !== "approved" && <Button variant="contained" color="primary" startIcon={<EditIcon />} component={RouterLink} to={`/betriebstagebuch/${entry._id}/edit`}>{t("editEntry")}</Button>}
              {isManager() && entry.status === "pending" && <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={() => setShowApproveModal(true)}>{t("approveEntry")}</Button>}
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadPDF} disabled={pdfDownloadLoading}>{pdfDownloadLoading ? <CircularProgress size={20} /> : t("downloadPDF")}</Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><InfoIcon color="action" />{t("generalInformation")}</Typography>
            <List dense disablePadding>
              <DetailItem label={t("entryDate")} value={moment(entry.date).format("LL")} />
              <DetailItem label={t("creator")} value={entry.creator?.name || t("unknown")} />
              <DetailItem label={t("createdAt")} value={moment(entry.createdAt).format("LLL")} />
              {entry.shiftStart && <DetailItem label={t("shiftStart")} value={entry.shiftStart} />}
              {entry.shiftEnd && <DetailItem label={t("shiftEnd")} value={entry.shiftEnd} />}
              {entry.weather && <DetailItem label={t("weather")} value={entry.weather} />}
              {entry.temperature && <DetailItem label={t("temperature")} value={`${entry.temperature}°C`} />}
              {entry.reviewerName && <DetailItem label={t("reviewedBy")} value={entry.reviewerName} icon={<VerifiedIcon />} />}
              {entry.reviewedAt && <DetailItem label={t("reviewedOn")} value={moment(entry.reviewedAt).format("LLL")} />}
            </List>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ height: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={activeTab} onChange={(e, newVal) => setActiveTab(newVal)} indicatorColor="primary" textColor="primary" variant="fullWidth">
                <Tab label={t("conveyorChecks")} icon={<ConveyorIcon />} iconPosition="start" id="tab-conveyors" />
                <Tab label={t("specialChecks")} icon={<SpecialCheckIcon />} iconPosition="start" id="tab-special" />
                <Tab label={t("notesAndSignatures")} icon={<NotesIcon />} iconPosition="start" id="tab-notes" />
              </Tabs>
            </Box>
            <TabPanel value={activeTab} index={0}>
              {entry.conveyorChecks && entry.conveyorChecks.length > 0 ? (
                <TableContainer><Table size="small">
                  <TableHead><TableRow><TableCell sx={{ width: '40%' }}>{t("conveyorName")}</TableCell><TableCell align="center" sx={{ width: '15%' }}>{t("status")}</TableCell><TableCell>{t("notes")}</TableCell></TableRow></TableHead>
                  <TableBody>{entry.conveyorChecks.map((c, i) => <TableRow key={i} hover><TableCell>{c.name}</TableCell><TableCell align="center">{renderCheckStatusIcon(c.functional)}</TableCell><TableCell><Typography variant="caption">{c.notes || '-'}</Typography></TableCell></TableRow>)}</TableBody>
                </Table></TableContainer>
              ) : <Typography color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>{t("noConveyorChecksRecorded")}</Typography>}
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              {entry.specialChecks && entry.specialChecks.length > 0 ? (
                <TableContainer><Table size="small">
                  <TableHead><TableRow><TableCell sx={{ width: '40%' }}>{t("checkName")}</TableCell><TableCell align="center" sx={{ width: '15%' }}>{t("status")}</TableCell><TableCell>{t("notes")}</TableCell></TableRow></TableHead>
                  <TableBody>{entry.specialChecks.map((c, i) => <TableRow key={i} hover><TableCell>{c.name}</TableCell><TableCell align="center">{renderCheckStatusIcon(c.completed)}</TableCell><TableCell><Typography variant="caption">{c.notes || '-'}</Typography></TableCell></TableRow>)}</TableBody>
                </Table></TableContainer>
              ) : <Typography color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>{t("noSpecialChecksRecorded")}</Typography>}
            </TabPanel>
            <TabPanel value={activeTab} index={2}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={entry.creatorSignature || entry.reviewerSignature ? 6 : 12}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="medium">{t("generalNotes")}</Typography>
                  {entry.generalNotes ? <Paper variant="outlined" sx={{ p: 2, whiteSpace: 'pre-wrap', minHeight: 100, bgcolor: alpha(theme.palette.info.main, 0.03) }}><Typography variant="body2">{entry.generalNotes}</Typography></Paper> : <Typography color="text.secondary">{t("noGeneralNotesProvided")}</Typography>}
                </Grid>
                {(entry.creatorSignature || entry.reviewerSignature) && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">{t("signatures")}</Typography>
                    {entry.creatorSignature && (<Box mb={1.5}><Typography variant="caption" color="text.secondary">{t("creatorSignature")} ({entry.creatorName || entry.creator?.name || 'N/A'}):</Typography><Paper variant="outlined" sx={{ p: 0.5, textAlign: 'center', mt: 0.5 }}><img src={entry.creatorSignature} alt="" style={{ maxWidth: '100%', maxHeight: 80, border: `1px solid ${theme.palette.divider}` }} /></Paper></Box>)}
                    {entry.reviewerSignature && (<Box><Typography variant="caption" color="text.secondary">{t("reviewerSignature")} ({entry.reviewerName || t("manager")}):</Typography><Paper variant="outlined" sx={{ p: 0.5, textAlign: 'center', mt: 0.5 }}><img src={entry.reviewerSignature} alt="" style={{ maxWidth: '100%', maxHeight: 80, border: `1px solid ${theme.palette.divider}` }} /></Paper></Box>)}
                  </Grid>
                )}
              </Grid>
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={showApproveModal} onClose={() => setShowApproveModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("approveEntryAsManager", { name: currentUser?.name })}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>{t("approveEntryConfirmation", { id: entry?._id.slice(-6) || 'N/A' })}</DialogContentText>
          {approveError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setApproveError(null)}>{approveError}</Alert>}
          {currentUser?.signature && <Button size="small" variant={useProfileSigForApproval ? "contained" : "outlined"} onClick={() => { setUseProfileSigForApproval(true); setSignatureData(currentUser.signature); setApproveError(null); }} startIcon={<AccountCircleIcon />} sx={{ mr: 1, mb: 1 }}>{t("useMyProfileSignature")}</Button>}
          <Button size="small" variant={!useProfileSigForApproval || !currentUser?.signature ? "contained" : "outlined"} onClick={() => { setUseProfileSigForApproval(false); setSignatureData(null); setApproveError(null); }} startIcon={<EditIcon />} sx={{ mb: 1 }}>{t("drawNewSignature")}</Button>

          {useProfileSigForApproval && currentUser?.signature ? (
            <Paper variant="outlined" sx={{ p: 1, mt: 1, textAlign: 'center', height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src={currentUser.signature} alt={t("profileSignatureAlt")} style={{ maxWidth: "100%", maxHeight: "150px", border: `1px solid ${theme.palette.divider}` }} /></Paper>
          ) : (
            <Paper variant="outlined" sx={{ borderColor: approveError && !useProfileSigForApproval ? "error.main" : "divider", height: 160, mt: 1, position: "relative" }}>
              <SignatureCanvas ref={signatureRef} penColor={theme.palette.text.primary} canvasProps={{ style: { width: "100%", height: "100%" } }} onBegin={() => setApproveError('')} />
              <IconButton onClick={() => { if (signatureRef.current) signatureRef.current.clear(); setSignatureData(null); setApproveError(null); }} size="small" sx={{ position: "absolute", top: 4, right: 4 }}><ClearIcon fontSize="small" /></IconButton>
            </Paper>
          )}
          {approveError && !useProfileSigForApproval && <FormHelperText error sx={{ mt: 0.5 }}>{approveError}</FormHelperText>}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button onClick={() => { setShowApproveModal(false); setApproveError(null); }} color="secondary">{t("cancel")}</Button>
          <Button variant="contained" onClick={handleApprove} disabled={approveLoading} startIcon={approveLoading ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}>{t("approve")}</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default BetriebstageBuchDetail;
